<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ $lang("derivations.title_update") }}</span>
    </v-card-title>
    <v-card-text>
      <v-container v-if="!initialized">
        <v-row justify="center">
          <div class="text-center">
            <v-progress-circular
              :size="70"
              :width="7"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
        </v-row>
      </v-container>
      <v-container v-if="initialized">
        <v-row>
          <v-col cols="12" xs="12" md="8">
            <h3>{{ $lang('derivations.panels.link_settings') }}</h3>
            <v-row>
              <v-col cols="12" xs="12" md="6">
                <v-autocomplete
                  v-model="form.room_type_id"
                  disabled
                  :items="roomTypes"
                  item-text="name"
                  item-value="id"
                  :label="$lang('derivations.fields.room_type')"
                  :hint="$lang('derivations.hints.room_type')"
                  persistent-hint
                  clearable
                  :error="!!validation.room_type_id"
                  :error-messages="
                    !!validation.room_type_id ? validation.room_type_id : []
                  "
                >
                  <template v-slot:selection="{ item }">
                    {{ item.name }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-html="item.name"></v-list-item-title>
                      <v-list-item-subtitle>
                        <v-icon>mdi-office-building</v-icon> {{ item.property.name }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" xs="12" md="6">
                <v-autocomplete
                  v-model="form.parent_id"
                  :items="roomTypes"
                  :label="$lang('derivations.fields.derived_from')"
                  :hint="$lang('derivations.hints.derived_from')"
                  persistent-hint
                  item-text="name"
                  item-value="id"
                  prepend-icon="mdi-home"
                  clearable
                  :error="!!validation.parent_id"
                  :error-messages="
                    !!validation.parent_id ? validation.parent_id : []
                  "
                >
                  <template v-slot:selection="{ item }">
                    {{ item.name }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-html="item.name"></v-list-item-title>
                      <v-list-item-subtitle>
                        <v-icon>mdi-office-building</v-icon> {{ item.property.name }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" xs="12" md="6">
                <h3>{{ $lang('derivations.panels.price_settings') }}</h3>
                <v-select
                  v-model="form.amount_status"
                  :items="$in('ConstDerivationRules')"
                  :label="$lang('derivations.fields.amount_status')"
                  :hint="$lang('derivations.hints.amount_status')"
                  persistent-hint
                  :error="!!validation.amount_status"
                  :error-messages="!!validation.amount_status ? validation.amount_status : []"
                  @input="checkChanges()"
                ></v-select>
                <v-text-field
                  v-if="form.amount_status !== 3"
                  v-model="form.amount"
                  prepend-inner-icon="mdi-cash"
                  :label="$lang('derivations.fields.amount')"
                  :hint="$lang('derivations.hints.amount')"
                  type="number"
                  :min="0"
                  :error="!!validation.amount"
                  :error-messages="!!validation.amount ? validation.amount : []"
                  @input="checkChanges()"
                ></v-text-field>
                <v-switch
                  v-if="form.amount_status !== 3"
                  v-model="form.is_round"
                  :label="$lang('derivations.fields.is_round')"
                  :hint="$lang('derivations.hints.is_round')"
                  persistent-hint
                  @change="checkChanges()"
                ></v-switch>
              </v-col>
              <v-col cols="12" xs="12" md="6">
                <h3>{{ $lang('derivations.panels.rate_settings') }}</h3>
                <v-switch
                  v-model="form.length_of_stay"
                  :label="$lang('derivations.fields.length_of_stay')"
                  :hint="$lang('derivations.hints.length_of_stay')"
                  :true-value="1"
                  :false-value="0"
                  persistent-hint
                  @change="checkChanges()"
                ></v-switch>
                <v-switch
                  v-model="form.restrictions"
                  :label="$lang('derivations.fields.restrictions')"
                  :hint="$lang('derivations.hints.restrictions')"
                  :true-value="1"
                  :false-value="0"
                  persistent-hint
                  @change="checkChanges()"
                ></v-switch>
                <v-switch
                  v-model="form.availability"
                  :label="$lang('derivations.fields.availability')"
                  :hint="$lang('derivations.hints.availability')"
                  :true-value="1"
                  :false-value="0"
                  persistent-hint
                  @change="checkChanges()"
                ></v-switch>
                <v-switch
                  v-model="form.stop_sell"
                  :label="$lang('derivations.fields.stop_sell')"
                  :hint="$lang('derivations.hints.stop_sell')"
                  :true-value="1"
                  :false-value="0"
                  persistent-hint
                  @change="checkChanges()"
                ></v-switch>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" xs="12" md="4">
            <v-card>
              <v-card-title>
                {{ $lang("derivations.helps.editing.title") }}
              </v-card-title>
              <v-card-text>
                {{ $lang("derivations.helps.editing.text") }}
                <v-boolean
                  v-model="form.force_regenerate"
                  :label="$lang('derivations.fields.force_regenerate')"
                  :hint="$lang('derivations.hints.force_regenerate')"
                  color="red"
                  persistent-hint
                  class="mr-2"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      
      <v-spacer></v-spacer>
      <v-btn color="success" @click="submit()" :loading="loading">
        {{ $lang("main.crud.update") }}
      </v-btn>
      <v-btn color="error" @click="cancel">
        {{ $lang("main.crud.cancel") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";

import {
  UPDATE_DERIVATIONS,
  UPDATE_DERIVATIONS_RESET,
  GET_DERIVATIONS_SINGLE
} from "@/store/derivations/actions";

import form from "@/pages/derivations/mixins/form";

export default {
  name: "EditingForm",
  mixins: [
    form
  ],
  props: {
    itemId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      old: {}
    }
  },
  beforeMount() {
    this.$store.dispatch(GET_DERIVATIONS_SINGLE, {
      id: this.itemId,
      params: {
        _data: {
          relations: {
            room_type: {
              selects: ["id", "name"]
            },
            parent: {
              selects: ["id", "name"]
            }
          }
        }
      }
    });
  },
  methods: {
    resetAction() {
      return UPDATE_DERIVATIONS_RESET;
    },
    submitAction() {
      return UPDATE_DERIVATIONS;
    },
    submitActionOptions() {
      return {
        id: this.itemId,
        params: this.form
      };
    },
    populate(item) {
      this.form = {
        ...this.form,
        ...item
      };

      this.old = {
        ...this.form,
        ...item
      };
    },
    checkChanges() {
      const shouldRegenerate = 
        this.old.amount !== this.form.amount ||
        this.old.amount_status !== this.form.amount_status ||
        [
          'availability',
          'length_of_stay',
          'restrictions',
          'stop_sell'
        ].some(key => this.old[key] === 0 && this.form[key] === 1);

      this.form.force_regenerate = shouldRegenerate ? 1 : 0;
    },
    track() {
      this.$monitorService.track(MixpanelEvents.DERIVATION_RULE_UPDATED);
    }
  },
  computed: {
    ...mapState({
      item: state => state.derivations.getSingleRequest.item,
      loading: state => state.derivations.editingRequest.loading,
      validation: state => state.derivations.editingRequest.validation
    })
  },
};
</script>
